import React, { useEffect, useRef, useState, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import "echarts-wordcloud";
import { getL1SheetProperty } from "../../../features/reduxworkbook/WorkbookSlice";
import { useSelector } from "react-redux";
import { getColourSchemeByID } from "../../../Colors/ColorPalettes";
import { getSheetControls } from "../../../features/reduxworkbook/WorkbookControllingSlice";
import { heatMapTransformData } from "../Utils/heatMapTransformData";
import { formatNumber } from "../Utils/FormatNumbers";

const HeatmapChartComp = ({
  Data,
  Sheetid,
  valueKeys,
  toolTipKeys,
  rowKeys: rawGroupByKeys,
  groupByKeys: rawRowKeys,
}) => {
    const customizationObject = useSelector((state) =>
        getL1SheetProperty(state, Sheetid, "customization")
    );

  const isAnimataionOff = useSelector(
    getSheetControls({ Sheetid: Sheetid, key: "isAnimataionOff" })
  );

    const {
        colorSchemeid,
        isLabelVisibleHeatmap,
        labelFontSizeHeatmap,
        labelFontColorHeatmap,
        isLegendHeatmapVisible,
        legendAlignHeatmap,
        legendVerticalAlignHeatmap,
        legendLayoutHeatmap,
        isTileVisible,
        isTitleBold,
        Title,
        TitleFontColor,
        TitleSize,
        valueAlign,
    } = customizationObject;

  const colorScheme = getColourSchemeByID(colorSchemeid);
  const COLORS = colorScheme?.colors || [];
  const containerRef = useRef(null);

  const rowKeys = rawRowKeys.map((item) => item.responseKey) || [];
  const groupByKeys = rawGroupByKeys.map((item) => item.responseKey) || [];

    const is3DMode = !!groupByKeys[0];
    const xKey = rowKeys[0];
    const yKey = is3DMode ? groupByKeys[0] : null;
    const valueKey = valueKeys[0]?.responseKey;
    const valueCaption = valueKeys[0]?.caption;

    const xAxisLabels = [...new Set(Data.map((d) => d[xKey]))];
    const yAxisLabels = is3DMode ? [...new Set(Data.map((d) => d[yKey]))] : [""];

  const heatmapData = Data.map((d) => [
    xAxisLabels.indexOf(d[xKey]),
    is3DMode ? yAxisLabels.indexOf(d[yKey]) : 0,
    d[valueKey],
  ]);

  const option = useMemo(() => {
    if (!xKey || !valueKey || !Data.length) return {};

    return {
      tooltip: {
        borderWidth: 0,
        padding: 0,
        extraCssText: "background: transparent; padding: 0; margin: 0;",
        position: function (point, params, dom, rect, size) {
          const tooltipHeight = size.contentSize[1];
          const top = point[1] - tooltipHeight - 10;
          return [point[0], top < 0 ? point[1] + 10 : top];
        },
        formatter: function (params) {
          const x = xAxisLabels[params.data[0]];
          const y = is3DMode ? yAxisLabels[params.data[1]] : "";
          const val = formatNumber(params.data[2], valueKeys[0]);

                    const additional = toolTipKeys
                        ?.map(({ responseKey, caption, numberFormat }) => {
                            const label = caption || responseKey;
                            const dataItem = Array.isArray(Data) ? Data[0] : Data;
                            const rawValue = dataItem?.[responseKey];
                            const isNumeric =
                                !isNaN(rawValue) && rawValue !== null && rawValue !== "";
                            const value = isNumeric ? Number(rawValue) : rawValue ?? "N/A";

                            const formattedValue = isNumeric
                                ? formatNumber(value, numberFormat)
                                : value;

              return `<div style="font-size: 15px; color: #4B5563;">${label}: ${formattedValue}</div>`;
            })
            .join("");

                    return `
                    <div style="background-color: transparent; padding: 0; margin: 0;">
                      <div style="
                        background: rgba(255, 255, 255, 0.85);
                        border-radius: 6px;
                        padding: 10px;
                        color: #374151;
                        font-family: sans-serif;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                      ">
                        <div style="font-weight: bold; font-size: 15px; margin-bottom: 4px;">${x}</div>
                        ${is3DMode
                            ? `<div style="font-weight: bold; font-size: 15px; margin-bottom: 4px;">${y}</div>`
                            : ""}
                        <div>${valueCaption}: ${val}</div>
                        <div>${additional}</div>
                      </div>
                    </div>`;
                },
            },
            grid: {
                top: "2%",  // Reduced from 10% to reduce space above chart
                bottom: "10%",
                containLabel: true,
            },
            xAxis: {
                type: "category",
                data: xAxisLabels,
                splitArea: { show: true },
            },
            yAxis: {
                type: "category",
                data: yAxisLabels,
                splitArea: { show: true },
            },
            visualMap: {
                show: isLegendHeatmapVisible,
                min: Math.min(...heatmapData.map((d) => d[2])),
                max: Math.max(...heatmapData.map((d) => d[2])),
                calculable: true,
                orient: legendLayoutHeatmap?.value || "horizontal",
                left: legendAlignHeatmap?.value || "right",
                top: legendVerticalAlignHeatmap?.value || "top",
                inRange: {
                    color: COLORS,
                },
            },
            series: [
                {

                    name: valueCaption,
                    type: "heatmap",
                    data: heatmapData,
                    label: {
                        show: isLabelVisibleHeatmap,
                        color: labelFontColorHeatmap || "#000",
                        fontSize: labelFontSizeHeatmap || 12,
                        formatter: function (params) {
                            return formatNumber(params.value[2], valueKeys[0]);
                        },
                    },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowColor: "rgba(0, 0, 0, 0.5)",
                        },
                    },
                },
            ],
        };
    }, [
        Data,
        xKey,
        yKey,
        valueKey,
        valueCaption,
        is3DMode,
        xAxisLabels,
        yAxisLabels,
        heatmapData,
        COLORS,
        isLabelVisibleHeatmap,
        labelFontColorHeatmap,
        labelFontSizeHeatmap,
        isLegendHeatmapVisible,
        legendAlignHeatmap,
        legendVerticalAlignHeatmap,
        legendLayoutHeatmap,
    ]);

  if (!Data.length || !xKey || !valueKey) return null;

    return (
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", marginTop: isTileVisible ? "2%" : "0%" }}>
            {/* Title Section */}

            {isTileVisible && (
                <div
                    className={`w-full ${isTitleBold ? "font-bold" : ""} ${valueAlign === "left"
                        ? "text-left"
                        : valueAlign === "right"
                            ? "text-right"
                            : "text-center"
                        }`}
                    style={{
                        fontSize: TitleSize,
                        color: TitleFontColor ?? "#bababa",
                        marginBottom: "0px",     // Ensure no extra space
                        paddingBottom: "0px",    // Remove bottom padding
                    }}
                >
                    {Title || " Title..."}
                </div>
            )}

            {/* Chart Section */}
            <div style={{ flex: 1 }}>
                <ReactECharts
                    option={option}
                    notMerge={true}
                    style={{ height: isTileVisible ? "100%" : "100%", width: "100%" }}
                />
            </div>
        </div>
    );
};

export default HeatmapChartComp;
